import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Users, DollarSign, FileText, RefreshCw, X, Gift, UserCheck, Wallet, Clock, TrendingUp, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="bg-white border border-[#E5E7EB] rounded-2xl p-5">
    <div className="flex items-center gap-2 text-[#4B5563] text-xs uppercase tracking-widest font-bold">
      <Icon className="w-4 h-4 text-[#D92D20]" /> {label}
    </div>
    <div className="font-display font-black text-3xl mt-2">{value}</div>
  </div>
);

function StudentRow({ s, onAction }) {
  const [busy, setBusy] = useState("");
  const [showComp, setShowComp] = useState(false);
  const [compPlan, setCompPlan] = useState("standard");

  const call = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); onAction(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  return (
    <tr className="border-t border-[#E5E7EB]" data-testid={`row-${s.user_id}`}>
      <td className="py-3 pr-3">
        <div className="font-semibold text-sm">{s.name}</div>
        <div className="text-xs text-[#4B5563]">{s.email}</div>
      </td>
      <td className="text-sm">{s.grade}<div className="text-xs text-[#4B5563]">{s.province}</div></td>
      <td className="text-sm text-center">{s.generations_this_month}</td>
      <td className="text-sm text-center">{s.tutor_messages_this_month}</td>
      <td className="text-sm text-center">{s.items_count}</td>
      <td className="text-sm">
        {s.subscription ? (
          <span className="text-[10px] font-bold uppercase tracking-widest bg-[#064E3B] text-white px-2 py-0.5 rounded-full" data-testid={`plan-${s.user_id}`}>
            {s.subscription.plan}{s.subscription.comp ? " (comp)" : ""}
          </span>
        ) : <span className="text-xs text-[#4B5563]">free trial</span>}
      </td>
      <td className="text-right">
        <div className="flex gap-1 justify-end flex-wrap">
          <button data-testid={`reset-${s.user_id}`} title="Reset monthly usage"
            disabled={busy === "reset"}
            onClick={() => call("reset", () => api.post(`/api/admin/tutor/students/${s.user_id}/reset-usage`, { generations: true, messages: true })
              .then(() => toast.success("Usage reset")))}
            className="text-xs px-2 py-1 rounded border border-[#E5E7EB] hover:bg-[#F3F4F6] inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Reset
          </button>
          <button data-testid={`comp-${s.user_id}`} title="Grant comp plan"
            onClick={() => setShowComp(!showComp)}
            className="text-xs px-2 py-1 rounded border border-[#E5E7EB] hover:bg-[#F3F4F6] inline-flex items-center gap-1">
            <Gift className="w-3 h-3" /> Comp
          </button>
          {s.subscription && (
            <>
              <button data-testid={`cancel-${s.user_id}`} title="Cancel subscription"
                disabled={busy === "cancel"}
                onClick={() => call("cancel", () => api.post(`/api/admin/tutor/students/${s.user_id}/cancel-subscription`)
                  .then(() => toast.success("Subscription cancelled")))}
                className="text-xs px-2 py-1 rounded border border-[#FECACA] text-[#D92D20] hover:bg-[#FEF2F2] inline-flex items-center gap-1">
                <X className="w-3 h-3" /> Cancel
              </button>
              {!s.subscription.comp && (
                <button data-testid={`refund-${s.user_id}`} title="Refund last invoice"
                  disabled={busy === "refund"}
                  onClick={() => { if (!window.confirm("Refund the most recent invoice?")) return;
                    call("refund", () => api.post(`/api/admin/tutor/students/${s.user_id}/refund-last`, {})
                      .then(({ data }) => toast.success(`Refunded $${(data.amount_cents/100).toFixed(2)}`))); }}
                  className="text-xs px-2 py-1 rounded border border-[#FECACA] text-[#D92D20] hover:bg-[#FEF2F2] inline-flex items-center gap-1">
                  <Wallet className="w-3 h-3" /> Refund
                </button>
              )}
            </>
          )}
          <button data-testid={`impersonate-${s.user_id}`} title="Impersonate (view their app)"
            disabled={busy === "imp"}
            onClick={() => call("imp", async () => {
              const { data } = await api.post(`/api/admin/tutor/students/${s.user_id}/impersonate`);
              // Save current admin session, swap to student, open new tab
              localStorage.setItem("tmo_admin_token_backup", localStorage.getItem("tmo_token") || "");
              localStorage.setItem("tmo_admin_user_backup", localStorage.getItem("tmo_user") || "");
              localStorage.setItem("tmo_token", data.access_token);
              localStorage.setItem("tmo_user", JSON.stringify(data.user));
              toast.success(`Impersonating ${data.user.email}. Open /tutor in a new tab.`);
              window.open("/tutor", "_blank");
            })}
            className="text-xs px-2 py-1 rounded border border-[#E5E7EB] hover:bg-[#F3F4F6] inline-flex items-center gap-1">
            <UserCheck className="w-3 h-3" /> View as
          </button>
        </div>
        {showComp && (
          <div className="mt-2 flex gap-1 justify-end" data-testid={`comp-form-${s.user_id}`}>
            <select value={compPlan} onChange={(e) => setCompPlan(e.target.value)}
              className="text-xs border border-[#E5E7EB] rounded px-2 py-1">
              <option value="starter">Starter</option>
              <option value="standard">Standard</option>
              <option value="premium">Premium</option>
            </select>
            <button onClick={() => call("comp-go", () => api.post(`/api/admin/tutor/students/${s.user_id}/grant-plan`,
                { plan: compPlan, days: 30 }).then(() => { toast.success("Plan granted"); setShowComp(false); }))}
              className="text-xs px-2 py-1 rounded bg-[#064E3B] text-white">Grant 30d</button>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminTutor() {
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("students"); // students | trials

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s1, s2] = await Promise.all([
        api.get("/api/admin/tutor/stats"),
        api.get(`/api/admin/tutor/students${q ? `?q=${encodeURIComponent(q)}` : ""}`),
      ]);
      setStats(s1.data);
      setStudents(s2.data);
    } catch (e) { toast.error("Could not load admin data"); }
    finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const adminBackup = localStorage.getItem("tmo_admin_token_backup");
  const restore = () => {
    localStorage.setItem("tmo_token", localStorage.getItem("tmo_admin_token_backup"));
    localStorage.setItem("tmo_user", localStorage.getItem("tmo_admin_user_backup"));
    localStorage.removeItem("tmo_admin_token_backup");
    localStorage.removeItem("tmo_admin_user_backup");
    window.location.reload();
  };

  if (loading) return <div className="p-6 text-sm text-[#4B5563]" data-testid="admin-tutor-loading">Loading…</div>;
  if (!stats) return <div className="p-6" data-testid="admin-tutor-error">Could not load.</div>;

  return (
    <div data-testid="admin-tutor">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-2xl">Teacher4Me · admin</h1>
        {adminBackup && (
          <button data-testid="admin-restore" onClick={restore} className="btn-outline text-xs">
            Restore admin session
          </button>
        )}
      </div>
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users}        label="Students onboarded"    value={stats.students_onboarded}    testid="stat-students" />
        <Stat icon={DollarSign}   label="Active subscriptions"  value={stats.active_subscriptions}  testid="stat-subs" />
        <Stat icon={DollarSign}   label="MRR (USD)"             value={`$${stats.mrr_usd}`}         testid="stat-mrr" />
        <Stat icon={FileText}     label="Items generated"       value={stats.items_generated}       testid="stat-items" />
      </div>

      <div className="mt-6 inline-flex border border-[#E5E7EB] rounded-full p-1 bg-white" data-testid="admin-tutor-tabs">
        <button data-testid="tab-students" onClick={() => setTab("students")}
          className={`text-xs font-bold px-4 py-1.5 rounded-full transition-colors ${tab === "students" ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:bg-[#F3F4F6]"}`}>
          Students
        </button>
        <button data-testid="tab-trials" onClick={() => setTab("trials")}
          className={`text-xs font-bold px-4 py-1.5 rounded-full transition-colors inline-flex items-center gap-1 ${tab === "trials" ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:bg-[#F3F4F6]"}`}>
          <Clock className="w-3 h-3" /> Free Trials
        </button>
      </div>

      {tab === "students" && (
      <>
      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="font-display font-bold">Students ({students.length})</div>
          <div className="flex gap-2">
            <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="search name or email…"
              className="border border-[#E5E7EB] rounded px-2 py-1.5 text-sm" />
            <button data-testid="admin-search-btn" onClick={load} className="btn-outline text-xs">Search</button>
          </div>
        </div>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-left text-sm" data-testid="admin-tutor-table">
            <thead className="text-xs uppercase tracking-widest text-[#4B5563]">
              <tr>
                <th className="py-2 pr-3">Student</th>
                <th>Grade</th>
                <th className="text-center">Gen / mo</th>
                <th className="text-center">Msgs / mo</th>
                <th className="text-center">Items</th>
                <th>Plan</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => <StudentRow key={s.user_id} s={s} onAction={load} />)}
              {students.length === 0 && (
                <tr><td colSpan="7" className="py-6 text-center text-[#4B5563]">No students yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-5">
        <div className="font-display font-bold">Plan breakdown</div>
        <ul className="mt-2 text-sm space-y-1" data-testid="plan-breakdown">
          {Object.entries(stats.plans_breakdown).map(([k, v]) => (
            <li key={k} className="flex justify-between"><span className="capitalize">{k}</span><b>{v}</b></li>
          ))}
        </ul>
        <Link to="/tutor" data-testid="admin-open-tutor" className="text-xs text-[#D92D20] font-semibold mt-3 inline-block">
          Open Teacher4Me as student →
        </Link>
      </div>
      </>
      )}

      {tab === "trials" && <TrialsTab />}
    </div>
  );
}


// ============== Free Trials tab ==============
function StatePill({ state }) {
  const map = {
    trialing: ["bg-[#FEF3C7] text-[#92400E]", "Trialing"],
    cancelling: ["bg-[#FEE2E2] text-[#7F1D1D]", "Cancelling"],
    converted: ["bg-[#D1FAE5] text-[#064E3B]", "Converted"],
    cancelled: ["bg-[#F3F4F6] text-[#4B5563]", "Cancelled"],
    past_due:  ["bg-[#FEE2E2] text-[#7F1D1D]", "Past due"],
  };
  const [cls, label] = map[state] || ["bg-[#F3F4F6] text-[#4B5563]", state];
  return <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

function TrialsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/tutor/trials");
      setData(data);
    } catch (e) { toast.error("Could not load trial data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const extend = async (userId) => {
    const raw = window.prompt("Extend trial by how many days? (1–60)", "7");
    if (!raw) return;
    const days = parseInt(raw, 10);
    if (!days || days < 1 || days > 60) { toast.error("Invalid days"); return; }
    setBusy(userId);
    try {
      const { data } = await api.post(`/api/admin/tutor/students/${userId}/extend-trial`, { days });
      toast.success(`Trial extended by ${data.days_added} days`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to extend"); }
    finally { setBusy(""); }
  };

  const cancel = async (userId) => {
    if (!window.confirm("Cancel this trial now? They keep access until trial end.")) return;
    setBusy(userId);
    try {
      await api.post(`/api/admin/tutor/students/${userId}/cancel-subscription`);
      toast.success("Trial set to cancel at period end");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };

  if (loading) return <div className="mt-6 p-6 text-sm text-[#4B5563]" data-testid="trials-loading">Loading trial data…</div>;
  if (!data) return <div className="mt-6 p-6" data-testid="trials-error">Could not load trials.</div>;

  const { active, recent_outcomes, stats } = data;

  return (
    <div data-testid="trials-tab">
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Clock}      label="Active trials"           value={stats.active_trials}                                  testid="trial-stat-active" />
        <Stat icon={TrendingUp} label="Conversion rate"          value={`${stats.conversion_rate_pct}%`}                       testid="trial-stat-conv" />
        <Stat icon={DollarSign} label="Projected MRR if all convert" value={`$${stats.projected_mrr_if_all_convert}`}        testid="trial-stat-mrr" />
        <Stat icon={Users}      label="Past converted / lost"   value={`${stats.converted} / ${stats.cancelled_or_expired}`} testid="trial-stat-outcomes" />
      </div>

      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-5">
        <div className="flex items-center justify-between">
          <div className="font-display font-bold">Active trials ({active.length})</div>
          <button data-testid="trials-refresh" onClick={load} className="btn-outline text-xs inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-left text-sm" data-testid="trials-active-table">
            <thead className="text-xs uppercase tracking-widest text-[#4B5563]">
              <tr>
                <th className="py-2 pr-3">Student</th>
                <th>Plan</th>
                <th className="text-center">Days left</th>
                <th>Trial ends</th>
                <th>State</th>
                <th className="text-center">Usage (gen / msgs)</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {active.map((t) => (
                <tr key={t.subscription_id} className="border-t border-[#E5E7EB]" data-testid={`trial-row-${t.user_id}`}>
                  <td className="py-3 pr-3">
                    <div className="font-semibold text-sm">{t.name || "—"}</div>
                    <div className="text-xs text-[#4B5563]">{t.email}</div>
                    <div className="text-[11px] text-[#4B5563]">{t.grade} · {t.province}</div>
                  </td>
                  <td className="text-sm">
                    <div className="capitalize font-semibold">{t.plan}</div>
                    <div className="text-xs text-[#4B5563]">${t.amount}/mo</div>
                  </td>
                  <td className="text-center text-sm" data-testid={`trial-days-${t.user_id}`}>
                    <span className={`font-bold ${t.days_remaining != null && t.days_remaining <= 2 ? "text-[#D92D20]" : "text-[#064E3B]"}`}>
                      {t.days_remaining ?? "—"}
                    </span>
                  </td>
                  <td className="text-xs text-[#4B5563]">{t.trial_end ? new Date(t.trial_end).toLocaleDateString() : "—"}</td>
                  <td><StatePill state={t.state} /></td>
                  <td className="text-center text-xs">{t.generations_this_month} / {t.tutor_messages_this_month}</td>
                  <td className="text-right">
                    <div className="flex gap-1 justify-end flex-wrap">
                      <button data-testid={`trial-extend-${t.user_id}`}
                        disabled={busy === t.user_id}
                        onClick={() => extend(t.user_id)}
                        className="text-xs px-2 py-1 rounded border border-[#E5E7EB] hover:bg-[#F3F4F6] inline-flex items-center gap-1">
                        <CalendarPlus className="w-3 h-3" /> Extend
                      </button>
                      {!t.cancel_at_period_end && (
                        <button data-testid={`trial-cancel-${t.user_id}`}
                          disabled={busy === t.user_id}
                          onClick={() => cancel(t.user_id)}
                          className="text-xs px-2 py-1 rounded border border-[#FECACA] text-[#D92D20] hover:bg-[#FEF2F2] inline-flex items-center gap-1">
                          <X className="w-3 h-3" /> Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {active.length === 0 && (
                <tr><td colSpan="7" className="py-6 text-center text-[#4B5563]">No active free trials right now.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-5">
        <div className="font-display font-bold">Recent trial outcomes ({recent_outcomes.length})</div>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-left text-sm" data-testid="trials-outcomes-table">
            <thead className="text-xs uppercase tracking-widest text-[#4B5563]">
              <tr>
                <th className="py-2 pr-3">Student</th>
                <th>Plan</th>
                <th>Trial ended</th>
                <th>Outcome</th>
                <th className="text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {recent_outcomes.map((t) => (
                <tr key={t.subscription_id} className="border-t border-[#E5E7EB]">
                  <td className="py-3 pr-3">
                    <div className="font-semibold text-sm">{t.name || "—"}</div>
                    <div className="text-xs text-[#4B5563]">{t.email}</div>
                  </td>
                  <td className="text-sm capitalize">{t.plan}</td>
                  <td className="text-xs text-[#4B5563]">{(t.ended_at || t.activated_at || t.trial_end) ? new Date(t.ended_at || t.activated_at || t.trial_end).toLocaleDateString() : "—"}</td>
                  <td><StatePill state={t.state} /></td>
                  <td className="text-right text-sm">${t.amount}</td>
                </tr>
              ))}
              {recent_outcomes.length === 0 && (
                <tr><td colSpan="5" className="py-6 text-center text-[#4B5563]">No completed trials yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
